import React, { useEffect, useState } from "react";
import axios from "axios";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import TextField from "@mui/material/TextField";
import Icon from "@mui/material/Icon";
import ApiEndpoints from "components/Api";
import ModeratorThemeProvider from "components/Moderator/ModeratorThemeProvider";
import useLoading from "components/Loading";
import useMessageBox from "components/MessageBox";
import TagSongsTable from "components/TagSongsTable";
import SongSelectModal from "components/SongSelectModal";
import "./TagsManagementPage.css";

const TagsManagementPage = () => {
  const [tags, setTags] = useState([]);
  const [selectedTag, setSelectedTag] = useState(null);
  const [showSongsModal, setShowSongsModal] = useState(false);
  const [showSongSelectModal, setShowSongSelectModal] = useState(false);
  const [editingTagId, setEditingTagId] = useState(null);
  const [editName, setEditName] = useState("");
  const [editIsPublic, setEditIsPublic] = useState(false);
  const [addingRow, setAddingRow] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [filter, setFilter] = useState("");
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  const [showLoading, hideLoading, Loading] = useLoading();
  const [showMessage, MessageBox] = useMessageBox();

  useEffect(() => {
    fetchTags();
  }, []);

  const fetchTags = async () => {
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      const response = await axios.get(ApiEndpoints.MODERATOR_TAGS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setTags(response.data);
      hideLoading();
    } catch (e) {
      hideLoading();
    }
  };

  const startEditTag = (tag) => {
    setEditingTagId(tag.tagId ?? tag.id);
    setEditName(tag.tagName ?? tag.name);
    setEditIsPublic(tag.tagIsPublic ?? tag.isPublic);
  };

  const cancelEditTag = () => {
    setEditingTagId(null);
    setEditName("");
    setEditIsPublic(false);
  };

  const cancelAddTag = () => {
    setAddingRow(false);
    setNewTagName("");
  };

  const addTag = async () => {
    if (!newTagName.trim()) return;
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      const response = await axios.post(
        ApiEndpoints.MODERATOR_TAGS,
        { name: newTagName },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hideLoading();
      showMessage("Dodano tag", "success");
      setTags((prev) => [...prev, response.data]);
      cancelAddTag();
    } catch (e) {
      hideLoading();
      showMessage("Błąd zapisu", "error");
    }
  };

  const updateTag = async () => {
    if (!editingTagId) return;
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      await axios.put(
        ApiEndpoints.MODERATOR_TAGS,
        {
          id: editingTagId,
          name: editName,
          isPublic: editIsPublic,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hideLoading();
      showMessage("Zaktualizowano tag", "success");
      cancelEditTag();
      fetchTags();
    } catch (e) {
      hideLoading();
      showMessage("Błąd zapisu", "error");
    }
  };

  const requestSort = (key) => {
    if (sortConfig.key === key) {
      if (sortConfig.direction === "ascending") {
        setSortConfig({ key, direction: "descending" });
      } else if (sortConfig.direction === "descending") {
        setSortConfig({ key: null, direction: null });
      } else {
        setSortConfig({ key, direction: "ascending" });
      }
    } else {
      setSortConfig({ key, direction: "ascending" });
    }
  };

  const sortedTags = [...tags];
  if (sortConfig.key) {
    sortedTags.sort((a, b) => {
      const keyMap = {
        id: "tagId",
        name: "tagName",
        isPublic: "tagIsPublic",
        songsCount: "songs",
      };
      const aKey = a[sortConfig.key] !== undefined ? sortConfig.key : keyMap[sortConfig.key];
      const bKey = b[sortConfig.key] !== undefined ? sortConfig.key : keyMap[sortConfig.key];
      const aVal = a[aKey];
      const bVal = b[bKey];
      if (aVal < bVal) return sortConfig.direction === "ascending" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "ascending" ? 1 : -1;
      return 0;
    });
  }

  const filteredTags = sortedTags.filter((t) =>
    (t.tagName ?? t.name).toLowerCase().includes(filter.toLowerCase())
  );

  const openSongsModal = (tag) => {
    setSelectedTag(tag);
    setShowSongsModal(true);
  };

  const closeSongsModal = () => {
    setShowSongsModal(false);
    setSelectedTag(null);
  };

  const openSongSelectModal = () => {
    setShowSongSelectModal(true);
  };

  const closeSongSelectModal = () => {
    setShowSongSelectModal(false);
  };

  const removeSongFromTag = async (song) => {
    if (!selectedTag) return;
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      const response = await axios.delete(
        ApiEndpoints.MODERATOR_TAG_SONG(
          selectedTag.tagId ?? selectedTag.id,
          song.id
        ),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hideLoading();
      setSelectedTag(response.data);
      setTags((prev) =>
        prev.map((t) =>
          (t.tagId ?? t.id) === response.data.id ? response.data : t
        )
      );
    } catch (e) {
      hideLoading();
    }
  };

  const addSongToTag = async (song) => {
    if (!selectedTag) return;
    try {
      const token = localStorage.getItem("authToken");
      showLoading();
      const response = await axios.post(
        ApiEndpoints.MODERATOR_TAG_SONG(
          selectedTag.tagId ?? selectedTag.id,
          song.id
        ),
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      hideLoading();
      setSelectedTag(response.data);
      setTags((prev) =>
        prev.map((t) =>
          (t.tagId ?? t.id) === response.data.id ? response.data : t
        )
      );
      closeSongSelectModal();
    } catch (e) {
      hideLoading();
    }
  };

  return (
    <ModeratorThemeProvider>
      <div className="tags-management-page-container">
        <Loading />
        <MessageBox />
        <TextField
          label="Filtruj"
          fullWidth
          margin="dense"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          className="tags-filter"
        />
        <div className="tags-actions">
          <Button
            color="success"
            variant="contained"
            size="small"
            onClick={() => {
              setNewTagName("");
              setAddingRow(true);
            }}
          >
            Dodaj
          </Button>
        </div>
        <Table className="tags-table">
          <TableHead>
            <TableRow>
              <TableCell className="sortable-header" onClick={() => requestSort("id")}>
                ID
                {sortConfig.key === "id" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("name")}>
                Nazwa
                {sortConfig.key === "name" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("isPublic")}>
                Publiczny
                {sortConfig.key === "isPublic" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell className="sortable-header" onClick={() => requestSort("songsCount")}>
                Liczba pieśni
                {sortConfig.key === "songsCount" && (
                  <Icon style={{ fontSize: "0.75rem", verticalAlign: "middle" }}>
                    {sortConfig.direction === "ascending" ? "arrow_upward" : "arrow_downward"}
                  </Icon>
                )}
              </TableCell>
              <TableCell>Akcje</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {addingRow && (
              <TableRow>
                <TableCell>Nowy</TableCell>
                <TableCell>
                  <TextField
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    size="small"
                  />
                </TableCell>
                <TableCell>-</TableCell>
                <TableCell>0</TableCell>
                <TableCell>
                  <Button
                    color="success"
                    variant="contained"
                    size="small"
                    onClick={addTag}
                  >
                    Zapisz
                  </Button>
                  <Button
                    color="success"
                    variant="outlined"
                    size="small"
                    style={{ marginLeft: "0.25rem" }}
                    onClick={cancelAddTag}
                  >
                    Anuluj
                  </Button>
                </TableCell>
              </TableRow>
            )}
            {filteredTags.map((tag) => {
              const id = tag.tagId ?? tag.id;
              const name = tag.tagName ?? tag.name;
              const isPublic = tag.tagIsPublic ?? tag.isPublic;
              const songsCount = tag.songs ? tag.songs.length : 0;
              return (
                <TableRow key={id}>
                  <TableCell>{id}</TableCell>
                  {editingTagId === id ? (
                    <>
                      <TableCell>
                        <TextField
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <input
                          type="checkbox"
                          id={`editIsPublic-${id}`}
                          checked={editIsPublic}
                          onChange={(e) => setEditIsPublic(e.target.checked)}
                        />
                      </TableCell>
                      <TableCell>{songsCount}</TableCell>
                      <TableCell>
                        <Button
                          color="success"
                          variant="contained"
                          size="small"
                          onClick={updateTag}
                        >
                          Zapisz
                        </Button>
                        <Button
                          color="success"
                          variant="outlined"
                          size="small"
                          style={{ marginLeft: "0.25rem" }}
                          onClick={cancelEditTag}
                        >
                          Anuluj
                        </Button>
                      </TableCell>
                    </>
                  ) : (
                    <>
                      <TableCell>{name}</TableCell>
                      <TableCell>{isPublic ? "Tak" : "Nie"}</TableCell>
                      <TableCell>{songsCount}</TableCell>
                      <TableCell>
                        <Button
                          color="success"
                          variant="contained"
                          size="small"
                          onClick={() => openSongsModal(tag)}
                        >
                          Pieśni
                        </Button>
                        <Button
                          color="success"
                          variant="contained"
                          size="small"
                          style={{ marginLeft: "0.25rem" }}
                          onClick={() => startEditTag(tag)}
                        >
                          Edytuj
                        </Button>
                      </TableCell>
                    </>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>

        <Dialog
          open={showSongsModal && !!selectedTag}
          onClose={closeSongsModal}
          maxWidth="lg"
          fullWidth
        >
          <DialogTitle>
            Pieśni tagu {selectedTag?.tagName ?? selectedTag?.name}
          </DialogTitle>
          <DialogContent>
            <TagSongsTable
              songs={selectedTag?.songs || []}
              onRemoveSong={removeSongFromTag}
              onAddSong={openSongSelectModal}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={closeSongsModal} color="success" variant="outlined" size="small">
              Zamknij
            </Button>
          </DialogActions>
        </Dialog>
        <SongSelectModal
          open={showSongSelectModal}
          onClose={closeSongSelectModal}
          onSelect={addSongToTag}
          songsEndpoint={selectedTag ? ApiEndpoints.MODERATOR_SONGS_WITHOUT_TAG(selectedTag.tagId ?? selectedTag.id) : null}
        />

      </div>
    </ModeratorThemeProvider>
  );
};

export default TagsManagementPage;
